import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  TemplateRef,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Subscription } from "rxjs";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";

import { sanitizeArrayRow } from "src/app/shared/utils/sanitize-export";
import { toGridColumns, toGridTotalItems } from "./data-grid.adapter";
import {
  DataGridCellDirective,
  DataGridDetailDirective,
  DataGridHeaderCellDirective,
} from "./data-grid-cell.directive";
import {
  GridColumn,
  GridEditing,
  GridGroupItem,
  GridHeaderNode,
  GridSaveEvent,
  GridToolbarItem,
  GridTotalItem,
} from "./data-grid.model";
import { GridColumnDirective } from "./grid-column.directive";

/** One rendered line: either a real record or a synthetic group header. */
interface GridRow {
  __group?: boolean;
  __groupFooter?: boolean;
  key?: string; // group identity, e.g. "status|Active"
  caption?: string; // "Status: Active"
  count?: number;
  data?: any; // the record, when __group is falsy
  /** dataField -> rendered aggregate, for a __groupFooter line. */
  agg?: Record<string, string>;
}

/**
 * Replacement for DevExtreme's <dx-data-grid>, built on ng-zorro's nz-table.
 *
 * WHY A SHARED COMPONENT AND NOT 239 HAND-CONVERSIONS
 * ---------------------------------------------------
 * The 239 grids in this app are near-identical in shape: 194 of 201 declare
 * `[remoteOperations]="false"` (all data already in memory), and the enabled
 * feature set clusters tightly - search 83%, export 86%, grouping 72%, column
 * chooser 68%, filter row 53%. So the work is one component that implements
 * that profile, after which each grid is a template swap.
 *
 * nz-table supplies the table shell, sorting affordances, pagination UI and
 * fixed-column scrolling. Everything DevExtreme had and nz-table does not -
 * the search panel, the filter row, the column chooser, grouping with group
 * headers and counts, footer summaries and the Excel export - is implemented
 * here.
 *
 * The pipeline is deliberately explicit and in one place:
 *   rows -> search -> per-column filters -> sort -> group -> page
 * DevExtreme applied the same order internally; keeping it identical is what
 * makes a converted screen show the same records in the same order.
 */
@Component({
  selector: "app-data-grid",
  templateUrl: "./data-grid.component.html",
  styleUrls: ["./data-grid.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class DataGridComponent
  implements OnChanges, AfterContentInit, OnDestroy
{
  constructor(private zone: NgZone) {}

  /** The records. Never mutated - every transform returns a new array. */
  @Input() dataSource: any[] = [];
  @Input() columns: GridColumn[] = [];
  /**
   * The app's existing `columnDefs` shape ({ field, headerName, alignment,
   * width, hide, fixed }), mapped internally by toGridColumns().
   *
   * This exists so a grid whose columns already come from a TypeScript array
   * converts with a TEMPLATE EDIT ONLY - no component change at all. The
   * alternative was making each component declare a second array and re-derive
   * it after every assignment to columnDefs, which is a real change to ~136
   * components and a real chance of the two arrays drifting apart.
   */
  @Input() legacyColumns?: any[];
  /** Unique row property. Only needed for selection / focused row. */
  @Input() keyExpr?: string;

  // --- feature switches, named after the dxo-* elements they replace --------
  @Input() searchPanel = true;
  @Input() filterRow = false;
  @Input() columnChooser = true;
  @Input() groupPanel = false;
  @Input() allowExport = true;
  @Input() exportFileName = "export";
  @Input() showBorders = true;
  @Input() showRowLines = true;
  @Input() rowAlternationEnabled = true;
  @Input() wordWrapEnabled = false;
  @Input() noDataText = "No data";
  @Input() pageSize = 10;
  @Input() allowedPageSizes: number[] = [10, 25, 50, 100];
  @Input() showPageSizeSelector = true;
  @Input() pagingEnabled = true;
  /** Horizontal scroll width, e.g. '1500px'. Needed when columns are fixed. */
  @Input() scrollX?: string;
  @Input() totalItems: GridTotalItem[] = [];
  /**
   * The app's existing summary-array shape ({ field, summaryType, format }),
   * mapped internally by toGridTotalItems().
   *
   * Same rationale as legacyColumns: ten grids built their footer from an
   * *ngFor over such an array, so this keeps each of them to one template
   * attribute instead of a new mapped field in ten components.
   */
  @Input() legacyTotalItems?: any[];
  /** dxi-group-item with showInGroupFooter - one aggregate row per group. */
  @Input() groupItems: GridGroupItem[] = [];
  @Input() toolbarItems: GridToolbarItem[] = [];
  @Input() loading = false;

  /** dxo-selection: 'none' | 'single' | 'multiple' (15 grids). */
  @Input() selectionMode: "none" | "single" | "multiple" = "none";
  /** Two-way, mirroring DevExtreme's [(selectedRowKeys)]. Requires keyExpr. */
  @Input() selectedRowKeys: any[] = [];
  /** dxo-master-detail (11 grids). Needs an <ng-template appGridDetail>. */
  @Input() masterDetail = false;
  /** dxo-header-filter: per-column distinct-value picker (6 grids). */
  @Input() headerFilter = false;
  /** dxo-filter-panel: shows active filters as removable chips (111 grids). */
  @Input() filterPanel = false;
  /**
   * A DevExtreme-shaped onCellPrepared handler, invoked per rendered cell.
   * Exists so the 32 existing handlers port without being rewritten - see
   * GridCellHookDirective.
   */
  @Input() cellPrepared?: (e: any) => void;
  /** DevExtreme-shaped onRowPrepared, invoked per rendered data row. */
  @Input() rowPrepared?: (e: any) => void;
  /**
   * Whether a given row may be selected. Replaces the `onEditorPreparing`
   * pattern used by asset-agreements and generate-payroll:
   *
   *   if (e.parentType === 'dataRow' && e.command === 'select')
   *       e.editorOptions.disabled = !e.row.data.shwChkBox;
   *
   * Expressed directly as a predicate rather than by reproducing DevExtreme's
   * editor-preparation event, because the intent is simply "this row's checkbox
   * is disabled".
   */
  @Input() isRowSelectable?: (row: any) => boolean;
  /** dxo-editing. Row mode only - see GridEditing. Requires keyExpr. */
  @Input() editing?: GridEditing;
  /** Two-way, mirroring DevExtreme's [(editRowKey)]. */
  @Input() editRowKey: any = null;

  @Output() rowClick = new EventEmitter<any>();
  /**
   * Deliberately DevExtreme-shaped. Existing handlers read `e.column.dataField`,
   * `e.data` and `e.columnIndex`; emitting those alongside `row` means a
   * converted grid keeps its click handler untouched. `columnIndex` in
   * particular is load-bearing - `getVehDashSldr` branches on
   * `evnt.columnIndex == 2`, and dropping it would silently disable that path.
   */
  @Output() cellClick = new EventEmitter<{
    row: any;
    data: any;
    column: GridColumn;
    columnIndex: number;
    value: any;
  }>();
  @Output() selectedRowKeysChange = new EventEmitter<any[]>();
  /**
   * DevExtreme's onSelectionChanged shape, NOT a bare array. Handlers written
   * against the grid do `this.selectedRowKeys = e.selectedRowKeys` - emitting the
   * array directly leaves that `undefined` and silently clears the selection.
   */
  @Output() selectionChanged = new EventEmitter<{
    selectedRowKeys: any[];
    selectedRowsData: any[];
  }>();
  @Output() editRowKeyChange = new EventEmitter<any>();
  /** DevExtreme's onSaved shape: { changes: [{ type, key, data }] }. */
  @Output() saved = new EventEmitter<GridSaveEvent>();

  @ContentChildren(DataGridCellDirective)
  cellTemplates!: QueryList<DataGridCellDirective>;
  @ContentChildren(DataGridHeaderCellDirective)
  headerTemplates!: QueryList<DataGridHeaderCellDirective>;
  @ContentChildren(DataGridDetailDirective)
  detailTemplates!: QueryList<DataGridDetailDirective>;
  /** <app-grid-column> children - columns declared in the template. */
  @ContentChildren(GridColumnDirective)
  projectedColumns!: QueryList<GridColumnDirective>;

  searchText = "";
  filterValues: Record<string, string> = {};
  sortField: string | null = null;
  sortDir: "asc" | "desc" = "asc";
  groupFields: string[] = [];
  pageIndex = 1;
  chooserOpen = false;

  /** Per-column distinct-value selections from the header filter. */
  headerFilterValues: Record<string, Set<any>> = {};
  expandedKeys = new Set<any>();

  private collapsed = new Set<string>();

  /** Rows after search+filter+sort, before grouping/paging. Drives summaries. */
  processed: any[] = [];
  /** What the table renders on the current page. */
  view: GridRow[] = [];
  total = 0;

  ngOnChanges(changes: SimpleChanges): void {
    // legacyColumns wins when supplied. Re-mapped on every change because the
    // host usually rebuilds columnDefs inside an API callback, i.e. after first
    // render - a one-shot map in ngOnInit would render an empty grid forever.
    if (changes["legacyColumns"] && this.legacyColumns) {
      this.columns = toGridColumns(this.legacyColumns);
      changes["columns"] = changes["legacyColumns"]; // fall through to the setup below
    }
    if (changes["columns"] && this.columns) {
      // groupIndex on a column is DevExtreme's "start grouped by this".
      this.groupFields = this.allColumns
        .filter(
          (c) =>
            c.groupIndex !== undefined && c.groupIndex !== null && c.dataField
        )
        .sort((a, b) => (a.groupIndex as number) - (b.groupIndex as number))
        .map((c) => c.dataField as string);

      const sorted = this.allColumns
        .filter((c) => c.sortOrder && c.dataField)
        .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))[0];
      if (sorted) {
        this.sortField = sorted.dataField as string;
        this.sortDir = sorted.sortOrder as any;
      }
    }
    this.rebuildColumns();
    if (changes["dataSource"] || changes["columns"]) {
      this.pageIndex = 1;
    }
    this.refresh();
  }

  // ---------------------------------------------------------------- columns
  /**
   * Array-driven columns plus any <app-grid-column> declared in the template,
   * appended after them - the order DevExtreme used, where a standalone
   * <dxi-column> followed the *ngFor.
   *
   * Rebuilt only when the inputs or the projected set change, NOT on every
   * change-detection pass: `columns` feeds `visibleColumns`, which the template
   * iterates for every header, cell and footer, and a fresh array each tick
   * would re-render the whole table continuously.
   */
  private projected: GridColumn[] = [];
  private projectionSub?: Subscription;

  ngAfterContentInit(): void {
    this.syncProjected();
    this.projectionSub = this.projectedColumns.changes.subscribe(() => {
      this.syncProjected();
      this.refresh();
    });
  }

  /**
   * Header layout, as a TREE. A band is a column with children (DevExtreme's
   * banded column group); a plain column is a leaf. Data cells are rendered
   * from LEAVES only - a band owns no data of its own.
   *
   * This was a flat `{ col, children }` list rendering a fixed two-row header,
   * which is all the app needed until `custom-grid` (six levels) and
   * `state-level-vehicle-dashboard` (three). Nesting is now unbounded.
   */
  headerTree: GridHeaderNode[] = [];

  private syncProjected(): void {
    const dirs = this.projectedColumns?.toArray() || [];
    // Build depth-first from the directive's `parent` links, preserving the
    // order the columns were declared in - the same order DevExtreme used.
    const build = (parent?: GridColumnDirective): GridHeaderNode[] =>
      dirs
        .filter((d) => d.parent === parent)
        .map((d) => this.node(d.toGridColumn(), build(d)));
    this.projectedTree = build(undefined);
    this.projected = this.projectedTree.flatMap((n) => this.leavesOf(n));
    this.rebuildColumns();
  }

  private projectedTree: GridHeaderNode[] = [];

  /** Assembles a node and computes its span from its children. */
  private node(col: GridColumn, children: GridHeaderNode[]): GridHeaderNode {
    return {
      col,
      children,
      colspan: children.length
        ? children.reduce((n, c) => n + c.colspan, 0)
        : 1,
      rowspan: 1, // set by measure(), which needs the finished tree's depth
    };
  }

  private leavesOf(n: GridHeaderNode): GridColumn[] {
    return n.children.length
      ? n.children.flatMap((c) => this.leavesOf(c))
      : [n.col];
  }

  /** Merged LEAF view: inputs first, template-declared columns after. */
  allColumns: GridColumn[] = [];

  /** totalItems, plus anything mapped from the legacy summary array. */
  get allTotalItems(): GridTotalItem[] {
    if (!this.legacyTotalItems) {
      return this.totalItems;
    }
    const key = JSON.stringify(
      this.legacyTotalItems.map((d: any) => [
        d && d.field,
        d && d.summaryType,
        d && d.displayFormat,
      ])
    );
    // The MERGED array has to be cached too, not just the mapped half. Spreading
    // on every read returns a new array each time, and this feeds an *ngFor in
    // the footer - so the footer re-rendered on every change-detection pass.
    // Exactly the mistake headerRows was written to avoid; caching the expensive
    // part while still allocating the cheap wrapper defeats the point.
    if (
      key !== this.legacyTotalsKey ||
      this.mergedTotalsSrc !== this.totalItems
    ) {
      this.legacyTotalsKey = key;
      this.mergedTotalsSrc = this.totalItems;
      this.mergedTotals = [
        ...this.totalItems,
        ...toGridTotalItems(this.legacyTotalItems),
      ];
    }
    return this.mergedTotals;
  }
  private legacyTotalsKey = " ";
  private mergedTotalsSrc: GridTotalItem[] | undefined = undefined;
  private mergedTotals: GridTotalItem[] = [];

  private rebuildColumns(): void {
    this.allColumns = [...(this.columns || []), ...this.projected];
    this.headerTree = [
      ...(this.columns || []).map((c) => this.node(c, [])),
      ...this.projectedTree,
    ];
  }

  /** Deepest nesting level; 1 for a grid with no bands. */
  private depthOf(nodes: GridHeaderNode[]): number {
    return nodes.length
      ? 1 + Math.max(0, ...nodes.map((n) => this.depthOf(n.children)))
      : 0;
  }

  /**
   * The header, level by level, with spans resolved against the visible tree.
   *
   * A leaf sitting above the bottom level gets `rowspan = depth - level` so it
   * reaches the last header row - without that, a grid mixing banded and plain
   * columns leaves a hole under every plain one.
   */
  get headerRows(): GridHeaderNode[][] {
    // Memoised on a signature of the tree AND its visibility flags. This is
    // read by an *ngFor, and *ngFor tracks by identity: recomputing it would
    // hand Angular fresh objects on every change-detection pass and re-render
    // the whole header, forever. Visibility is genuinely dynamic here
    // ([visible]="indctr==4"), so it has to be part of the key rather than
    // computed once.
    const key = this.headerSignature(this.headerTree);
    if (key !== this.headerRowsKey) {
      this.headerRowsKey = key;
      this.headerRowsCache = this.buildHeaderRows();
    }
    return this.headerRowsCache;
  }
  private headerRowsKey = " ";
  private headerRowsCache: GridHeaderNode[][] = [];

  private headerSignature(nodes: GridHeaderNode[]): string {
    return nodes
      .map(
        (n) =>
          (n.col.visible === false ? "-" : "+") +
          (n.col.caption || n.col.dataField || "") +
          (n.children.length
            ? "(" + this.headerSignature(n.children) + ")"
            : "")
      )
      .join(",");
  }

  private buildHeaderRows(): GridHeaderNode[][] {
    const visible = this.pruneHidden(this.headerTree);
    // At least one row even with no columns: the header still carries the
    // select-all checkbox and the Actions cell, and columnDefs commonly arrive
    // after the first render.
    const depth = Math.max(1, this.depthOf(visible));
    const rows: GridHeaderNode[][] = Array.from({ length: depth }, () => []);
    const walk = (nodes: GridHeaderNode[], level: number) => {
      for (const n of nodes) {
        rows[level].push({
          ...n,
          rowspan: n.children.length ? 1 : depth - level,
        });
        if (n.children.length) {
          walk(n.children, level + 1);
        }
      }
    };
    walk(visible, 0);
    return rows;
  }

  /**
   * Drops hidden columns and re-derives spans from what is left. A band whose
   * children are all hidden is dropped with them; a band that started with
   * children and lost them all does NOT become a data leaf, because it has no
   * dataField to render.
   */
  private pruneHidden(nodes: GridHeaderNode[]): GridHeaderNode[] {
    const out: GridHeaderNode[] = [];
    for (const n of nodes) {
      if (n.col.visible === false) {
        continue;
      }
      if (!n.children.length) {
        out.push(this.node(n.col, []));
        continue;
      }
      const kids = this.pruneHidden(n.children);
      if (kids.length) {
        out.push(this.node(n.col, kids));
      }
    }
    return out;
  }

  get visibleColumns(): GridColumn[] {
    return this.allColumns.filter((c) => c.visible !== false);
  }
  get chooserColumns(): GridColumn[] {
    return this.allColumns.filter((c) => c.showInColumnChooser !== false);
  }
  get groupableColumns(): GridColumn[] {
    return this.allColumns.filter(
      (c) => c.dataField && c.allowGrouping !== false
    );
  }

  /** Raw underlying value - what sorting, filtering and export all read. */
  value(row: any, col: GridColumn): any {
    if (!row) {
      return "";
    }
    if (col.calculateCellValue) {
      return col.calculateCellValue(row);
    }
    return col.dataField ? row[col.dataField] : "";
  }
  /** Display value. Export deliberately does NOT use this (see exportExcel). */
  display(row: any, col: GridColumn): any {
    const v = this.value(row, col);
    if (col.lookup) {
      return this.lookupLabel(col, v);
    }
    return this.applyFormat(col.format, v, row);
  }

  /**
   * Applies a column format in any of the shapes DevExtreme accepted.
   *
   * `format` was typed - and called - as a bare function, but 11 template
   * bindings and 8 columnDefs arrays pass an OBJECT. The adapter carries it
   * through on its `...d` spread, so both routes reached `col.format(v, row)`
   * and threw at RENDER time, not at compile: the build was green and the grid
   * was blank.
   *
   * Locale is pinned to 'en' rather than left to the browser: no DevExtreme
   * `locale()` call exists in this app, so every one of these numbers has always
   * been formatted as en. Passing `undefined` would silently re-group INR
   * amounts per the viewer's locale (en-IN renders lakhs, not thousands).
   */
  private applyFormat(fmt: GridColumn["format"], v: any, row?: any): any {
    if (!fmt || v === null || v === undefined || v === "") {
      return v;
    }
    if (typeof fmt === "function") {
      return fmt(v, row);
    }

    const n = typeof v === "number" ? v : Number(v);
    if (!isFinite(n)) {
      return v;
    } // non-numeric cell: show it unchanged

    const { type, precision, ...intl } = fmt as any;
    if (type) {
      const digits = precision ?? (type === "fixedPoint" ? 0 : 2);
      const style =
        type === "currency"
          ? "currency"
          : type === "percent"
          ? "percent"
          : "decimal";
      return new Intl.NumberFormat("en", {
        style,
        ...(style === "currency" ? { currency: intl.currency || "INR" } : {}),
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
        ...intl,
      }).format(n);
    }
    return new Intl.NumberFormat("en", intl).format(n);
  }

  /** Resolves a lookup's dataSource, which may be an array or a function. */
  lookupItems(col: GridColumn): any[] {
    const ds = col.lookup?.dataSource;
    const arr = typeof ds === "function" ? ds() : ds;
    return Array.isArray(arr) ? arr : [];
  }

  /** dxo-lookup: id -> label. Falls back to the raw value if unmatched. */
  lookupLabel(col: GridColumn, v: any): any {
    const lk = col.lookup;
    if (!lk) {
      return v;
    }
    // Loose equality on purpose: these ids arrive as numbers from one endpoint
    // and strings from another, and a strict match would blank the cell.
    const hit = this.lookupItems(col).find((o) => o && o[lk.valueExpr] == v);
    return hit ? hit[lk.displayExpr] : v;
  }

  cellTemplateFor(name?: string): TemplateRef<any> | null {
    if (!name || !this.cellTemplates) {
      return null;
    }
    const d = this.cellTemplates.find((t) => t.name === name);
    return d ? d.template : null;
  }
  headerTemplateFor(name?: string): TemplateRef<any> | null {
    if (!name || !this.headerTemplates) {
      return null;
    }
    const d = this.headerTemplates.find((t) => t.name === name);
    return d ? d.template : null;
  }

  widthOf(col: GridColumn): string | null {
    if (col.width === undefined || col.width === null) {
      return null;
    }
    return typeof col.width === "number" ? col.width + "px" : String(col.width);
  }

  // ------------------------------------------------------------- user input
  onSearch(text: string): void {
    this.searchText = text;
    this.pageIndex = 1;
    this.refresh();
  }

  onFilter(col: GridColumn, text: string): void {
    if (!col.dataField) {
      return;
    }
    if (text) {
      this.filterValues[col.dataField] = text;
    } else {
      delete this.filterValues[col.dataField];
    }
    this.pageIndex = 1;
    this.refresh();
  }

  onSort(col: GridColumn): void {
    if (!col.dataField || col.allowSorting === false) {
      return;
    }
    if (this.sortField === col.dataField) {
      this.sortDir = this.sortDir === "asc" ? "desc" : "asc";
    } else {
      this.sortField = col.dataField;
      this.sortDir = "asc";
    }
    this.refresh();
  }

  toggleColumn(col: GridColumn): void {
    col.visible = col.visible === false;
    this.refresh();
  }

  // ---- header filter (dxo-header-filter) ---------------------------------
  /** Distinct values for a column, taken from the UNFILTERED source. */
  distinctValues(col: GridColumn): any[] {
    const seen = new Set<any>();
    for (const r of this.dataSource || []) {
      seen.add(this.value(r, col) ?? "");
    }
    return Array.from(seen).sort((a, b) =>
      String(a).localeCompare(String(b), undefined, {
        numeric: true,
        sensitivity: "base",
      })
    );
  }
  isHeaderFilterChecked(col: GridColumn, v: any): boolean {
    const set = col.dataField ? this.headerFilterValues[col.dataField] : null;
    return !set || set.size === 0 || set.has(v); // nothing selected = show all
  }
  toggleHeaderFilter(col: GridColumn, v: any): void {
    if (!col.dataField) {
      return;
    }
    const key = col.dataField;
    if (!this.headerFilterValues[key]) {
      this.headerFilterValues[key] = new Set<any>();
    }
    const set = this.headerFilterValues[key];
    if (set.has(v)) {
      set.delete(v);
    } else {
      set.add(v);
    }
    if (!set.size) {
      delete this.headerFilterValues[key];
    }
    this.pageIndex = 1;
    this.refresh();
  }

  // ---- filter panel (dxo-filter-panel) ------------------------------------
  /** Active filters as chips - what the DevExtreme filter panel displayed. */
  get activeFilters(): Array<{ field: string; label: string }> {
    const out: Array<{ field: string; label: string }> = [];
    for (const f of Object.keys(this.filterValues)) {
      const col = this.allColumns.find((c) => c.dataField === f);
      out.push({
        field: f,
        label: (col?.caption || f) + ": " + this.filterValues[f],
      });
    }
    for (const f of Object.keys(this.headerFilterValues)) {
      const col = this.allColumns.find((c) => c.dataField === f);
      const n = this.headerFilterValues[f].size;
      out.push({
        field: f,
        label: (col?.caption || f) + ": " + n + " selected",
      });
    }
    return out;
  }
  clearFilter(field: string): void {
    delete this.filterValues[field];
    delete this.headerFilterValues[field];
    this.pageIndex = 1;
    this.refresh();
  }
  clearAllFilters(): void {
    this.filterValues = {};
    this.headerFilterValues = {};
    this.searchText = "";
    this.pageIndex = 1;
    this.refresh();
  }

  // ---- selection (dxo-selection) ------------------------------------------
  private keyOf(row: any): any {
    return this.keyExpr ? row?.[this.keyExpr] : row;
  }
  /** Template-visible alias of keyOf, for the master-detail context. */
  keyOfRow(row: any): any {
    return this.keyOf(row);
  }
  isSelected(row: any): boolean {
    return this.selectedRowKeys.includes(this.keyOf(row));
  }
  toggleRow(row: any, checked: boolean): void {
    const k = this.keyOf(row);
    if (this.selectionMode === "single") {
      this.selectedRowKeys = checked ? [k] : [];
    } else {
      this.selectedRowKeys = checked
        ? [...this.selectedRowKeys, k]
        : this.selectedRowKeys.filter((x) => x !== k);
    }
    this.emitSelection();
  }
  get allOnPageSelected(): boolean {
    const rows = this.view.filter((l) => !l.__group);
    return rows.length > 0 && rows.every((l) => this.isSelected(l.data));
  }
  toggleAllOnPage(checked: boolean): void {
    const keys = this.view
      .filter((l) => !l.__group)
      .map((l) => this.keyOf(l.data));
    this.selectedRowKeys = checked
      ? Array.from(new Set([...this.selectedRowKeys, ...keys]))
      : this.selectedRowKeys.filter((k) => !keys.includes(k));
    this.emitSelection();
  }
  private emitSelection(): void {
    this.selectedRowKeysChange.emit(this.selectedRowKeys);
    const key = this.keyExpr;
    // With no keyExpr the key IS the row object - DevExtreme behaves the same
    // way, so selectedRowKeys and selectedRowsData coincide.
    const rows = key
      ? (this.dataSource || []).filter((r) =>
          this.selectedRowKeys.includes(r[key])
        )
      : this.selectedRowKeys;
    this.selectionChanged.emit({
      selectedRowKeys: this.selectedRowKeys,
      selectedRowsData: rows,
    });
  }

  // ---- master detail (dxo-master-detail) ----------------------------------
  get detailTemplate(): TemplateRef<any> | null {
    return this.detailTemplates?.first
      ? this.detailTemplates.first.template
      : null;
  }
  isExpanded(row: any): boolean {
    return this.expandedKeys.has(this.keyOf(row));
  }
  toggleExpand(row: any): void {
    const k = this.keyOf(row);
    if (this.expandedKeys.has(k)) {
      this.expandedKeys.delete(k);
    } else {
      this.expandedKeys.add(k);
    }
  }

  /** Extra leading columns (expander, checkbox) - drives colspan arithmetic. */
  get leadingColumnCount(): number {
    return (
      (this.masterDetail ? 1 : 0) + (this.selectionMode !== "none" ? 1 : 0)
    );
  }

  /**
   * Every cell in a full-width row (group header, detail row) must span this.
   * Leading columns AND the trailing actions column both count - miss either and
   * the row comes up short, which renders as a ragged right edge rather than an
   * error.
   */
  get spanAllColumns(): number {
    return (
      this.visibleColumns.length +
      this.leadingColumnCount +
      (this.showActionsColumn ? 1 : 0)
    );
  }

  // ---- editing (dxo-editing, row mode) ------------------------------------
  /** Working copy of the row under edit. Cancel simply discards it. */
  editBuffer: any = null;

  get editingEnabled(): boolean {
    return (
      !!this.editing &&
      (this.editing.allowUpdating === true ||
        this.editing.allowDeleting === true)
    );
  }
  get showActionsColumn(): boolean {
    return this.editingEnabled;
  }
  get canUpdate(): boolean {
    return this.editing?.allowUpdating === true;
  }
  get canDelete(): boolean {
    return this.editing?.allowDeleting === true;
  }

  isEditing(row: any): boolean {
    return (
      this.editRowKey !== null &&
      this.editRowKey !== undefined &&
      this.keyOf(row) === this.editRowKey
    );
  }

  startEdit(row: any): void {
    if (!this.canUpdate) {
      return;
    }
    // Edit a COPY. DevExtreme's row mode does the same - the grid's data is only
    // touched once the host's save succeeds - so Cancel must not have mutated
    // the record the table is still rendering.
    this.editBuffer = { ...row };
    this.editRowKey = this.keyOf(row);
    this.editRowKeyChange.emit(this.editRowKey);
  }

  cancelEdit(): void {
    this.editBuffer = null;
    this.editRowKey = null;
    this.editRowKeyChange.emit(null);
  }

  saveEdit(): void {
    if (!this.editBuffer) {
      return;
    }
    const key = this.editRowKey;
    const data = this.editBuffer;
    // Emit BEFORE clearing: handlers read editRowKey off the host component
    // (asset-issues does exactly that), so it must still be set when they run.
    this.saved.emit({ changes: [{ type: "update", key, data }] });
    this.editBuffer = null;
    this.editRowKey = null;
    this.editRowKeyChange.emit(null);
  }

  removeRow(row: any): void {
    if (!this.canDelete) {
      return;
    }
    const msg =
      this.editing?.confirmDeleteMessage === undefined
        ? "Are you sure you want to delete this record?"
        : this.editing?.confirmDeleteMessage;
    if (msg && !confirm(msg)) {
      return;
    }
    this.saved.emit({
      changes: [{ type: "remove", key: this.keyOf(row), data: row }],
    });
  }

  /** A column is editable unless it opts out, and never the key column. */
  isColumnEditable(col: GridColumn): boolean {
    if (col.allowEditing === false) {
      return false;
    }
    if (!col.dataField) {
      return false;
    }
    if (this.keyExpr && col.dataField === this.keyExpr) {
      return false;
    }
    return true;
  }

  editorType(col: GridColumn): "number" | "date" | "text" {
    if (col.dataType === "number") {
      return "number";
    }
    if (col.dataType === "date") {
      return "date";
    }
    return "text";
  }

  // ---- drag column resize (allowColumnResizing) ---------------------------
  //
  // Reordering is deliberately NOT implemented - decided 2026-08-10. 181 grids
  // enabled both, but NO grid uses <dxo-state-storing>, so a dragged layout was
  // already discarded on navigation. Resize is the half users actually reach for
  // (a column too narrow to read); reorder is the half that interacts with fixed
  // columns and column-chooser ordering, for no durable benefit.

  private static readonly MIN_COL_PX = 40;
  private resizing: { col: GridColumn; startX: number; startW: number } | null =
    null;
  private resizeRaf = 0;
  /** Width applied during the drag; committed to col.width on mouseup. */
  resizingWidth = 0;

  get isResizing(): boolean {
    return !!this.resizing;
  }

  startResize(e: MouseEvent, col: GridColumn, th: HTMLElement): void {
    if (col.allowResizing === false) {
      return;
    }
    // Stop the click reaching the header, which would sort the column.
    e.preventDefault();
    e.stopPropagation();
    this.resizing = {
      col,
      startX: e.clientX,
      startW: th.getBoundingClientRect().width,
    };
    // Outside the zone: a drag fires mousemove continuously, and letting each
    // one trigger change detection over a full page of rows makes the drag
    // stutter. Re-entered only when a frame actually commits a new width.
    this.zone.runOutsideAngular(() => {
      document.addEventListener("mousemove", this.onResizeMove);
      document.addEventListener("mouseup", this.onResizeEnd);
    });
  }

  private onResizeMove = (e: MouseEvent): void => {
    if (!this.resizing) {
      return;
    }
    const next = Math.max(
      DataGridComponent.MIN_COL_PX,
      this.resizing.startW + (e.clientX - this.resizing.startX)
    );
    if (this.resizeRaf) {
      return;
    } // coalesce to one commit per frame
    this.resizeRaf = requestAnimationFrame(() => {
      this.resizeRaf = 0;
      if (!this.resizing) {
        return;
      }
      this.zone.run(() => {
        this.resizing!.col.width = Math.round(next);
      });
    });
  };

  private onResizeEnd = (): void => {
    this.teardownResize();
  };

  private teardownResize(): void {
    document.removeEventListener("mousemove", this.onResizeMove);
    document.removeEventListener("mouseup", this.onResizeEnd);
    if (this.resizeRaf) {
      cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = 0;
    }
    if (this.resizing) {
      this.zone.run(() => {
        this.resizing = null;
      });
    }
  }

  ngOnDestroy(): void {
    this.projectionSub?.unsubscribe();
    // A component destroyed mid-drag would otherwise leave two document-level
    // listeners behind, holding a reference to it for the life of the page.
    this.teardownResize();
  }

  /**
   * Header caption for a grouped field, for the "Grouped by" chips.
   *
   * The chips previously showed the raw dataField - `asrt_nm`, `sno` - which is
   * a database column name, not something a user of the portal recognises.
   */
  captionOf(field: string): string {
    const col = this.allColumns.find((c) => c.dataField === field);
    return (col && (col.caption || col.dataField)) || field;
  }

  addGroup(field: string): void {
    if (field && !this.groupFields.includes(field)) {
      this.groupFields.push(field);
      this.pageIndex = 1;
      this.refresh();
    }
  }
  /**
   * Drops all grouping, replacing DevExtreme's `instance.clearGrouping()`.
   * Distinct from removeGroup(), which drops one field.
   */
  clearGrouping(): void {
    if (!this.groupFields.length) {
      return;
    }
    this.groupFields = [];
    this.pageIndex = 1;
    this.refresh();
  }
  removeGroup(field: string): void {
    this.groupFields = this.groupFields.filter((f) => f !== field);
    this.pageIndex = 1;
    this.refresh();
  }
  toggleGroupRow(key: string): void {
    if (this.collapsed.has(key)) {
      this.collapsed.delete(key);
    } else {
      this.collapsed.add(key);
    }
    this.refresh();
  }
  isCollapsed(key: string): boolean {
    return this.collapsed.has(key);
  }

  onPageIndex(i: number): void {
    this.pageIndex = i;
    this.refresh();
  }
  onPageSize(size: number): void {
    this.pageSize = size;
    this.pageIndex = 1;
    this.refresh();
  }

  // ------------------------------------------------------------------ pipe
  /**
   * Recomputes the rendered rows. PUBLIC because two components hold a
   * @ViewChild on the grid and drove it imperatively through DevExtreme's
   * `dataGrid.instance.refresh()`; there has to be an equivalent or those calls
   * have nowhere to go.
   */
  refresh(): void {
    let rows = Array.isArray(this.dataSource) ? this.dataSource.slice() : [];
    rows = this.applySearch(rows);
    rows = this.applyFilters(rows);
    rows = this.applyHeaderFilters(rows);
    rows = this.applySort(rows);
    this.processed = rows;
    this.total = rows.length;

    const lines = this.groupFields.length
      ? this.buildGroups(rows)
      : rows.map((r) => ({ data: r } as GridRow));
    this.view = this.pagingEnabled
      ? lines.slice(
          (this.pageIndex - 1) * this.pageSize,
          this.pageIndex * this.pageSize
        )
      : lines;
    // Paging over grouped LINES, not records, is what DevExtreme does too -
    // otherwise a group header can land on one page and its rows on the next.
    this.total = this.pagingEnabled ? lines.length : lines.length;
  }

  private applySearch(rows: any[]): any[] {
    const q = (this.searchText || "").trim().toLowerCase();
    if (!q) {
      return rows;
    }
    // allowSearch === false opts a column out of the search panel, as it did in
    // DevExtreme. Absent means searchable, so grids that never set it are
    // unaffected.
    const cols = this.visibleColumns.filter((c) => c.allowSearch !== false);
    return rows.filter((r) =>
      cols.some((c) =>
        String(this.value(r, c) ?? "")
          .toLowerCase()
          .includes(q)
      )
    );
  }

  private applyFilters(rows: any[]): any[] {
    const keys = Object.keys(this.filterValues);
    if (!keys.length) {
      return rows;
    }
    return rows.filter((r) =>
      keys.every((k) => {
        const col = this.allColumns.find((c) => c.dataField === k);
        const want = String(this.filterValues[k]).toLowerCase();
        const got = String(
          (col ? this.value(r, col) : r[k]) ?? ""
        ).toLowerCase();
        return got.includes(want);
      })
    );
  }

  /** Header-filter selections. An empty set for a column means "show all". */
  private applyHeaderFilters(rows: any[]): any[] {
    const fields = Object.keys(this.headerFilterValues);
    if (!fields.length) {
      return rows;
    }
    return rows.filter((r) =>
      fields.every((f) => {
        const set = this.headerFilterValues[f];
        if (!set || !set.size) {
          return true;
        }
        const col = this.allColumns.find((c) => c.dataField === f);
        return set.has((col ? this.value(r, col) : r[f]) ?? "");
      })
    );
  }

  private applySort(rows: any[]): any[] {
    if (!this.sortField) {
      return rows;
    }
    const col = this.allColumns.find((c) => c.dataField === this.sortField);
    if (!col) {
      return rows;
    }
    const dir = this.sortDir === "asc" ? 1 : -1;
    return rows.slice().sort((a, b) => {
      const x = this.value(a, col),
        y = this.value(b, col);
      if (x === y) {
        return 0;
      }
      if (x === null || x === undefined || x === "") {
        return 1;
      } // blanks last, both directions
      if (y === null || y === undefined || y === "") {
        return -1;
      }
      if (col.dataType === "number") {
        return (Number(x) - Number(y)) * dir;
      }
      if (col.dataType === "date") {
        return (new Date(x).getTime() - new Date(y).getTime()) * dir;
      }
      return (
        String(x).localeCompare(String(y), undefined, {
          numeric: true,
          sensitivity: "base",
        }) * dir
      );
    });
  }

  /** Single-level grouping - covers every grouped grid in the app today. */
  private buildGroups(rows: any[]): GridRow[] {
    const field = this.groupFields[0];
    const col = this.allColumns.find((c) => c.dataField === field);
    const caption = col?.caption || field;
    const buckets = new Map<string, any[]>();
    for (const r of rows) {
      const k = String((col ? this.value(r, col) : r[field]) ?? "");
      if (!buckets.has(k)) {
        buckets.set(k, []);
      }
      (buckets.get(k) as any[]).push(r);
    }
    const out: GridRow[] = [];
    for (const [k, group] of buckets) {
      const key = field + "|" + k;
      out.push({
        __group: true,
        key,
        caption: caption + ": " + (k || "(blank)"),
        count: group.length,
      });
      if (!this.collapsed.has(key)) {
        group.forEach((r) => out.push({ data: r }));
        // dxi-group-item [showInGroupFooter] - aggregates over THIS group only.
        if (this.groupItems.length) {
          out.push({
            __groupFooter: true,
            key: key + "|foot",
            agg: this.aggregate(group, this.groupItems),
          });
        }
      }
    }
    return out;
  }

  /** Shared aggregate maths for group footers and the grid footer. */
  private aggregate(
    rows: any[],
    items: Array<GridGroupItem | GridTotalItem>
  ): Record<string, string> {
    const out: Record<string, string> = {};
    for (const item of items) {
      const col = this.allColumns.find((c) => c.dataField === item.column);
      const vals = rows.map((r) => (col ? this.value(r, col) : r[item.column]));
      const nums = vals.map(Number).filter((n) => !isNaN(n));
      let v: number;
      switch (item.summaryType) {
        case "count":
          v = rows.length;
          break;
        case "sum":
          v = nums.reduce((a, b) => a + b, 0);
          break;
        case "avg":
          v = nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
          break;
        case "min":
          v = nums.length ? Math.min(...nums) : 0;
          break;
        case "max":
          v = nums.length ? Math.max(...nums) : 0;
          break;
        default:
          v = 0;
      }
      // An explicit valueFormat wins; otherwise trim float noise from sum/avg
      // without touching integers.
      const text = (item as any).valueFormat
        ? String(this.applyFormat((item as any).valueFormat, v))
        : Number.isInteger(v)
        ? String(v)
        : v.toFixed(2);
      out[item.column] = item.displayFormat
        ? item.displayFormat.replace("{0}", text)
        : text;
    }
    return out;
  }

  // -------------------------------------------------------------- summaries
  summaryValue(item: GridTotalItem): string {
    const col = this.allColumns.find((c) => c.dataField === item.column);
    const nums = this.processed.map((r) =>
      col ? this.value(r, col) : r[item.column]
    );
    let v: number;
    switch (item.summaryType) {
      case "count":
        v = this.processed.length;
        break;
      case "sum":
        v = nums.reduce((a, b) => a + (Number(b) || 0), 0);
        break;
      case "avg":
        v = nums.length
          ? nums.reduce((a, b) => a + (Number(b) || 0), 0) / nums.length
          : 0;
        break;
      case "min":
        v = Math.min(...nums.map(Number).filter((n) => !isNaN(n)));
        break;
      case "max":
        v = Math.max(...nums.map(Number).filter((n) => !isNaN(n)));
        break;
      default:
        v = 0;
    }
    const text = item.valueFormat
      ? String(this.applyFormat(item.valueFormat, v))
      : String(v);
    return item.displayFormat ? item.displayFormat.replace("{0}", text) : text;
  }

  // ----------------------------------------------------------------- export
  /**
   * Excel export, replacing DevExtreme's `exportDataGrid({ customizeCell:
   * sanitizeExcelCell, ... })`.
   *
   * SECURITY (audit obs #9 - Formula Injection, CWE-1236): every value written
   * goes through sanitizeArrayRow AT the addRow call. That placement is not
   * cosmetic - scripts/formula-injection-scan.js proves a sink safe by finding
   * the sanitiser inline at the sink or assigned to the identifier in the same
   * file, so sanitising into a temporary elsewhere would still be correct but
   * would land as WARN ("a human has to read it") rather than PASS.
   *
   * Exports the RAW value, not the formatted one: a date or number must stay a
   * date or number in the sheet, and the display formatter returns a string.
   * This matches what exportDataGrid did.
   *
   * Exports every FILTERED row, not just the visible page - DevExtreme's
   * behaviour, and what officers expect from an export button.
   */
  exportExcel(): void {
    const cols = this.visibleColumns.filter((c) => c.allowExporting !== false);
    const workbook = new Workbook();
    const sheet = workbook.addWorksheet("Sheet1");

    sheet.addRow(
      sanitizeArrayRow(cols.map((c) => c.caption || c.dataField || ""))
    );
    sheet.getRow(1).font = { bold: true };

    for (const row of this.processed) {
      sheet.addRow(
        sanitizeArrayRow(
          cols.map((c) => {
            const v = this.value(row, c);
            return v === null || v === undefined ? "" : v;
          })
        )
      );
    }

    cols.forEach((c, i) => {
      const w = typeof c.width === "number" ? Math.round(c.width / 7) : 18;
      sheet.getColumn(i + 1).width = Math.max(10, Math.min(60, w));
    });

    workbook.xlsx.writeBuffer().then((buffer) => {
      saveAs(
        new Blob([buffer], { type: "application/octet-stream" }),
        (this.exportFileName || "export") + ".xlsx"
      );
    });
  }

  // ------------------------------------------------------------------ misc
  /**
   * DevExtreme's onRowClick handed the handler an EVENT OBJECT, not the record:
   * every converted screen reads `$event.data`. Emitting the bare record made
   * `$event.data` undefined, so each of those handlers silently did nothing -
   * a row click looked dead. `cellClick` below already emits the event shape;
   * this was the odd one out.
   */
  onRowClick(row: any, rowIndex = 0, event?: Event): void {
    this.rowClick.emit({
      data: row,
      key: this.keyOf(row),
      rowIndex,
      rowType: "data",
      values: this.visibleColumns.map((c) => this.value(row, c)),
      isSelected: this.isSelected(row),
      event,
    });
  }
  onCellClick(row: any, col: GridColumn): void {
    this.cellClick.emit({
      row,
      data: row,
      column: col,
      columnIndex: this.visibleColumns.indexOf(col),
      value: this.value(row, col),
    });
  }
  trackByIndex(i: number): number {
    return i;
  }
}
